import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { Candidate, CandidateFilter, MunicipalCandidate, TabulationPermission, User, VoterPreferenceFilters } from '../../../types';
import React, { useEffect, useState } from 'react';
import {
  fetchBarangaysByMunicipality,
  fetchMunicipalitiesByDistrict,
  fetchPrecinctsByBarangay
} from '../../../utils/locationUtils';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchAllCandidates } from '../../../utils/candidates';
import useAuth from '../../../hooks/useAuth';

interface VoterPreferenceFormProps {
  onGenerateReport: (filters: VoterPreferenceFilters) => void;
}

const VoterPreferenceForm: React.FC<VoterPreferenceFormProps> = ({ onGenerateReport }) => {
  const { user } = useAuth();
  
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [barangays, setBarangays] = useState<string[]>([]);
  const [precincts, setPrecincts] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  const [filters, setFilters] = useState<VoterPreferenceFilters>({
    region: 'REGION XIII',
    province: 'SURIGAO DEL NORTE',
    district: 'DISTRICT 1',
    municipality: '',
    barangay: '', // Optional now
    precinct: '',
    candidateFilters: [{
      position: '',
      candidate: '',
      matchType: 'match'
    }]
  });

  useEffect(() => {
    const loadInitialData = async (user: User) => {
      try {
        const loadedCandidates = await fetchAllCandidates();
        setCandidates(loadedCandidates);

        const districtMunicipalities = await fetchMunicipalitiesByDistrict(
          filters.region, 
          filters.province, 
          filters.district
        );

        // Filter municipalities based on user's allowedTabulations if set
        const filteredMunicipalities = user.allowedTabulations 
          ? districtMunicipalities.filter(municipality => 
              user.allowedTabulations?.some(
                (permission: TabulationPermission) => 
                  permission.type === 'municipal' && permission.id === municipality
              )
            )
          : districtMunicipalities;

        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    if (user) {
      loadInitialData(user);
    }
  }, [user]);

  const handleMunicipalityChange = async (municipality: string) => {
    try {
      const newBarangays = await fetchBarangaysByMunicipality(
        filters.region, 
        filters.province, 
        filters.district, 
        municipality
      );
      setBarangays(newBarangays);
      setFilters(prev => ({
        ...prev,
        municipality,
        barangay: '', // Reset barangay
        precinct: '',
        candidateFilters: [{
          position: '',
          candidate: '',
          matchType: 'match'
        }]
      }));
      // Reset subsequent dropdowns
      setPrecincts([]);
    } catch (error) {
      console.error('Error fetching barangays:', error);
    }
  };

  const handleBarangayChange = async (barangay: string) => {
    try {
      const newPrecincts = await fetchPrecinctsByBarangay(
        filters.region, 
        filters.province, 
        filters.district, 
        filters.municipality, 
        barangay
      );
      setPrecincts(newPrecincts);
      setFilters(prev => ({
        ...prev,
        barangay,
        precinct: ''
      }));
    } catch (error) {
      console.error('Error fetching precincts:', error);
    }
  };

  const handleCandidateFilterChange = (
    filterIndex: number, 
    field: keyof CandidateFilter, 
    value: string
  ) => {
    const updatedFilters = [...filters.candidateFilters];
    updatedFilters[filterIndex] = {
      ...updatedFilters[filterIndex],
      [field]: value
    };

    setFilters(prev => ({
      ...prev,
      candidateFilters: updatedFilters
    }));
  };

  const addCandidateFilter = () => {
    setFilters(prev => ({
      ...prev,
      candidateFilters: [
        ...prev.candidateFilters, 
        { position: '', candidate: '', matchType: 'match' }
      ]
    }));
  };

  const removeCandidateFilter = (indexToRemove: number) => {
    if (filters.candidateFilters.length > 1) {
      setFilters(prev => ({
        ...prev,
        candidateFilters: prev.candidateFilters.filter((_, index) => index !== indexToRemove)
      }));
    }
  };

  const handleSubmit = () => {
    // Validate mandatory fields (Barangay is now optional)
    if (!filters.region || !filters.province || !filters.district || 
        !filters.municipality) {
      alert('Please fill in all required fields');
      return;
    }

    onGenerateReport(filters);
  };

  // Get unique positions from candidates
  const availablePositions = Array.from(new Set(
    candidates.map(candidate => candidate.position)
  ));

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {/* Municipality Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Municipality *</InputLabel>
          <Select
            value={filters.municipality}
            label="Municipality *"
            onChange={(e) => handleMunicipalityChange(e.target.value)}
            disabled={!filters.district}
            required
          >
            {municipalities.map(municipality => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Barangay Dropdown (Now Optional) */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Barangay (Optional)</InputLabel>
          <Select
            value={filters.barangay}
            label="Barangay (Optional)"
            onChange={(e) => handleBarangayChange(e.target.value)}
            disabled={!filters.municipality}
          >
            <MenuItem value="">All Barangays</MenuItem>
            {barangays.map(barangay => (
              <MenuItem key={barangay} value={barangay}>
                {barangay}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Optional Precinct Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Precinct (Optional)</InputLabel>
          <Select
            value={filters.precinct || ''}
            label="Precinct (Optional)"
            onChange={(e) => setFilters(prev => ({
              ...prev,
              precinct: e.target.value
            }))}
            disabled={!filters.barangay}
          >
            <MenuItem value="">All Precincts</MenuItem>
            {precincts.map(precinct => (
              <MenuItem key={precinct} value={precinct}>
                {precinct}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Candidate Filters */}
      {filters.candidateFilters.map((candidateFilter, filterIndex) => (
        <React.Fragment key={filterIndex}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              Candidate Filter {filterIndex + 1}
              {filters.candidateFilters.length > 1 && (
                <IconButton 
                  color="error" 
                  size="small" 
                  onClick={() => removeCandidateFilter(filterIndex)}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Typography>
          </Grid>

          {/* Position Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Candidate Position</InputLabel>
              <Select
                value={candidateFilter.position}
                onChange={(e) => handleCandidateFilterChange(
                  filterIndex, 
                  'position', 
                  e.target.value
                )}
                label="Candidate Position"
                disabled={!filters.municipality}
              >
                {availablePositions.map((position) => (
                  <MenuItem key={position} value={position}>
                    {position}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Candidate Match Type Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Candidate Match Type</InputLabel>
              <Select
                value={candidateFilter.matchType}
                label="Candidate Match Type"
                onChange={(e) => handleCandidateFilterChange(
                  filterIndex, 
                  'matchType', 
                  e.target.value
                )}
                disabled={!filters.municipality}
              >
                <MenuItem value="match">Matches Selected Candidate</MenuItem>
                <MenuItem value="different">Different from Selected Candidate</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Candidate Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Selected Candidate</InputLabel>
              <Select
                value={candidateFilter.candidate}
                onChange={(e) => handleCandidateFilterChange(
                  filterIndex, 
                  'candidate', 
                  e.target.value
                )}
                label="Candidate"
                disabled={!filters.municipality || !candidateFilter.position}
              >
                {candidates
                  .filter((candidate) => 
                    ((candidate.type !== 'municipal') || ((candidate.type === 'municipal') && ((candidate as MunicipalCandidate).city_municipality === filters.municipality))) &&
                    (candidate.position === candidateFilter.position)
                  )
                  .map((candidate) => (
                    <MenuItem key={candidate.name} value={candidate.name}>
                      {candidate.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        </React.Fragment>
      ))}

      {/* Add Candidate Filter Button */}
      <Grid item xs={12}>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={addCandidateFilter}
          disabled={!filters.municipality}
        >
          Add Candidate Filter
        </Button>
      </Grid>

      {/* Submit Button */}
      <Grid item xs={12}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={!filters.region || !filters.province || 
                    !filters.district || !filters.municipality}
        >
          Generate Report
        </Button>
      </Grid>
    </Grid>
  );
};

export default VoterPreferenceForm;
